<template>
  <div class="annotation" ref="annotation" :style="annotationStyle">
    <section class="annotation__section">
      <canvas
        :class="['annotation__canvas', { 'annotation__canvas--hidden': isHideCanvas }]"
        ref="annotation__canvas"
        id="annotation__canvas"
      ></canvas>
      <div v-if="isPublished" class="annotation__canvas--disabled" @click="checkIsWorkflowPublished" />
      <div class="annotation__video">
        <d-video-secure
          ref="annotationVideoPlayer"
          crossorigin="anonymous"
          :poster="videoPoster"
          :signedSrc="videoSource"
          @ended="handleVideoEnd"
          @timeupdate="handleVideoOnTimeUpdate()"
          @waiting="isShowSpinner = true"
          @canplay="isShowSpinner = false"
        />
        <div class="annotation__spinner">
          <v-progress-circular v-if="isShowSpinner || !isFontReady" :size="70" color="white" indeterminate />
        </div>
      </div>
      <div class="annotation__time-line">
        <builder-annotation-timeline
          :currentTime="currentTime"
          :selectedId="watch.selectedId"
          :videoEndTime="videoEndTime"
          :sortedDataArray="sortedDataArray"
          :isHideCanvas="isHideCanvas"
          :isPlaying="isVideoPlaying"
          :isPublished="isPublished"
          @handleNoteSwitch="handleNoteSwitch"
          @handleNoteSelect="handleNoteSelect"
          @handleNoteTimeUpdate="handleNoteTimeUpdate"
          @handleVideoCurrentTimeUpdate="handleVideoCurrentTimeUpdate"
          @handleVideoPlayOrPause="handleVideoPlayOrPause"
        />
      </div>
      <div v-if="isShowNoteTool" class="annotation__target-tool" v-bind="v_bind_annotation_tool">
        <BuilderAnnotationTool
          :config="selectedNote"
          :currentStepId="currentStepId"
          @handleNoteStyleChange="handleNoteStyleChange"
        />
      </div>
      <div
        v-for="blurItem in blurItems"
        :key="blurItem.id"
        class="blurItem"
        :style="blurItem.style"
        @click="handleClickBlurItem(blurItem.id)"
      />
    </section>
    <div class="annotation__hr" />
    <div class="annotation__rightBlock">
      <div class="annotation__control mb-2">
        <div
          v-for="typeKey in annotationTypeKeys"
          :key="typeKey"
          :class="[
            'annotation__control-btn dh-btn',
            type === typeKey && (type === 'select' ? !!watch.selectedId : true) ? 'btn-primary' : 'btn-secondary',
          ]"
          @click="switchType(typeKey, { from: 'tab' })"
        >
          <font-awesome-icon :icon="iconTypeMap[typeKey]" />
          <div
            v-if="type === typeKey && [ANNOTATION_TYPE.BLUR, ANNOTATION_TYPE.SHAPE].includes(typeKey) && !shapeType"
            class="shapeTypeSelector"
          >
            <div
              v-for="shape in getShapeIcons(typeKey)"
              class="shapeIcon"
              :key="shape.value"
              @click.stop="handleClickShape(shape.value)"
            >
              <font-awesome-icon :icon="shape.icon" />
            </div>
          </div>
        </div>
      </div>
      <builder-annotation-notes
        :isPublished="isPublished"
        :selectedId="watch.selectedId"
        :sortedDataArray="sortedDataArray"
        :videoEndTime="videoEndTime"
        @handleNoteDelete="handleNoteDelete"
        @handleNoteSelect="handleNoteSelect"
        @handleNoteTimeUpdate="handleNoteTimeUpdate"
      />
      <input ref="fileUpload" type="file" accept="image/jpeg,  image/png" @change="handleImageChange" hidden />
      <DConfirmationPopup
        v-model="isShowImageCountLimitAlert"
        :isAllowCancel="false"
        :title="$t('builder.annotationImageCountLimitTitle')"
        :actionBtnName="$t('all.ok')"
        :content="$t('builder.annotationImageCountLimitDescription')"
        @clickActionBtn="isShowImageCountLimitAlert = false"
      />
      <DConfirmationPopup
        v-model="isShowImageSizeLimitAlert"
        :isAllowCancel="false"
        :title="$t('builder.annotationImageSizeLimitTitle')"
        :actionBtnName="$t('all.ok')"
        :content="$t('builder.annotationImageSizeLimitDescription')"
        @clickActionBtn="isShowImageSizeLimitAlert = false"
      />
    </div>
  </div>
</template>

<script>
import BuilderAnnotationNotes from "./BuilderAnnotationNotes";
import BuilderAnnotationTimeline from "./BuilderAnnotationTimeline";
import BuilderAnnotationTool from "./BuilderAnnotationTool";
import DConfirmationPopup from "@/components/DPopup/DConfirmationPopup.vue";
import DVideoSecure from "@/components/ui_components/DVideoSecure.vue";

import { AnnotationFabric } from "@/js/annotation/AnnotationFabric";
import { CANVAS_HEIGHT, CANVAS_WIDTH, ANNOTATION_ICON_MAP, ANNOTATION_TYPE, SHAPE } from "@/constants/annotationStatus";
import { mapGetters, mapState, mapMutations } from "vuex";
import { debounce } from "lodash-es";

import { checkNotoSerifFontFamilyLoaded, getBlurItems } from "@/js/annotation";
import { toRound } from "@/js/common/formatNumber";
import environment from "@/../env";

export default {
  name: "BuilderAnnotation",
  props: {
    videoEndTime: Number,
    videoPoster: String,
    videoSource: Object,
    currentStepId: {
      type: String,
      default: () => "",
    },
    madeChanges: {
      type: Boolean,
      required: true,
    },
    isPublished: {
      type: Boolean,
      default: () => false,
    },
  },
  data() {
    const annotationConfig = {
      type: ANNOTATION_TYPE.SELECT,
      shapeType: "",
      isShowImageCountLimitAlert: false,
      isShowImageSizeLimitAlert: false,
    };
    const playerConfig = {
      currentTime: 0,
      isHideCanvas: false,
      isShowSpinner: true,
      isVideoPlaying: false,
      videoWidth: CANVAS_WIDTH,
      videoHeight: CANVAS_HEIGHT,
    };
    const toolStyle = {
      top: 0,
      left: 0,
      display: "none",
    };

    const annotationTypeKeys = Object.keys(ANNOTATION_ICON_MAP);
    return {
      ANNOTATION_TYPE,
      annotationTypeKeys:
        environment.backendType === "ali"
          ? annotationTypeKeys.filter((type) => type !== ANNOTATION_TYPE.BLUR)
          : annotationTypeKeys,
      SHAPE,
      watch: {
        selectedId: null,
      },
      ...annotationConfig,
      ...playerConfig,
      isFontReady: false,
      toolStyle,
      iconTypeMap: ANNOTATION_ICON_MAP,
      AnnotationFabric: null,
    };
  },
  components: {
    BuilderAnnotationNotes,
    BuilderAnnotationTimeline,
    BuilderAnnotationTool,
    DVideoSecure,
    DConfirmationPopup,
  },
  async mounted() {
    this.handleWindowResize();
    window.addEventListener("resize", this.handleWindowResize);
    document.addEventListener("keydown", this.handleKeyboard);
    this.AnnotationFabric = new AnnotationFabric({
      canvasId: "annotation__canvas",
      sourceCanvas: this.$refs["annotation__canvas"],
      videoEndTime: this.videoEndTime,
      watch: this.watch,
      isHideCanvas: this.isHideCanvas,
    });
    this.AnnotationFabric.Fabric.on("object:added", this.handleAddDataIntoDataArray);
    this.AnnotationFabric.Fabric.on("object:modified", this.handleObjectChange);
    this.AnnotationFabric.Fabric.on("text:changed", debounce(this.handleObjectChange, 500));
    this.AnnotationFabric.Fabric.on("selection:cleared", this.handleEditorToolClear);
    this.AnnotationFabric.Fabric.on("selection:created", this.handleEditorToolPopup);
    this.AnnotationFabric.Fabric.on("selection:updated", this.handleEditorToolPopup);
    this.AnnotationFabric.Fabric.on("object:moving", this.handleEditorToolPopup);

    this.isFontReady = await checkNotoSerifFontFamilyLoaded();
  },
  beforeDestroy() {
    window.removeEventListener("resize", this.handleWindowResize);
    document.removeEventListener("keydown", this.handleKeyboard);
  },
  computed: {
    ...mapState("annotation", ["annotations"]),
    ...mapGetters("annotation", ["getters_get_annotation_by_id"]),
    annotationStyle() {
      return {
        "--video-width": `${this.videoWidth}px`,
        "--video-height": `${this.videoHeight}px`,
      };
    },
    blurItems() {
      const ratio = this.videoHeight / CANVAS_HEIGHT;
      return getBlurItems(this.dataArray, ratio, this.currentTime, this.watch.selectedId);
    },
    isShowNoteTool() {
      return this.selectedNote && this.selectedNote.type !== ANNOTATION_TYPE.IMAGE;
    },
    selectedNote() {
      const data = this.sortedDataArray.find(({ id }) => id === this.watch.selectedId);
      return data;
    },
    sortedDataArray() {
      return this.dataArray.sort((a, b) => a.startTime - b.startTime);
    },
    v_bind_annotation_tool() {
      return {
        style: {
          top: this.toolStyle.top + "px",
          left: this.toolStyle.left + "px",
          position: "absolute",
          display: this.toolStyle.display,
          "z-index": 30,
        },
      };
    },
    dataArray() {
      const targetAnnotation = this.getters_get_annotation_by_id(this.currentStepId);
      return targetAnnotation?.elements ? [...targetAnnotation.elements] : [];
    },
  },
  methods: {
    ...mapMutations("annotation", [
      "ADD_ANNOTATIONS",
      "DELETE_ANNOTATION",
      "MUTATE_ANNOTATION_PROPERTY",
      "MUTATE_ANNOTATION_CANVAS_SIZE",
    ]),
    checkIsWorkflowPublished() {
      if (this.isPublished) {
        this.$emit("showEditWorkflowPopup");
      }
      return this.isPublished;
    },
    checkIsOverImageCountLimit() {
      const imageAnnotationCount = this.annotations.reduce((result, stepAnnotation) => {
        const imageAnnotationItems = stepAnnotation.elements.filter(({ type }) => type === ANNOTATION_TYPE.IMAGE);
        return result + imageAnnotationItems.length;
      }, 0);
      return imageAnnotationCount >= 30;
    },
    getShapeIcons(type) {
      const shapes = [
        {
          value: SHAPE.CIRCLE,
          icon: ["far", "circle"],
        },
        {
          value: SHAPE.RECTANGLE,
          icon: ["far", "square"],
        },
      ];
      return type === ANNOTATION_TYPE.BLUR
        ? shapes
        : [
            ...shapes,
            {
              value: SHAPE.TRIANGLE,
              icon: ["far", "triangle"],
            },
          ];
    },
    switchType(type, { from, lineWidth, color } = {}) {
      const previousType = this.type;
      if (this.checkIsWorkflowPublished()) return;
      if (type === ANNOTATION_TYPE.IMAGE && this.checkIsOverImageCountLimit()) {
        this.isShowImageCountLimitAlert = true;
        return;
      }
      if (this.isHideCanvas) this.handleNoteSwitch();
      this.type = type;
      this.AnnotationFabric.toggleDrawMode(type === ANNOTATION_TYPE.PEN);
      if (type === ANNOTATION_TYPE.IMAGE) {
        this.$refs.fileUpload.click();
      } else if ([ANNOTATION_TYPE.SHAPE, ANNOTATION_TYPE.BLUR].includes(type)) {
        this.shapeType = "";
      } else {
        this.AnnotationFabric[type]({ lineWidth, color });
      }
      if (from === "tab" && type === ANNOTATION_TYPE.SELECT) {
        this.AnnotationFabric.unselect();
      }
    },
    scaleValueToDefaultCanvas(value) {
      const ratio = this.videoHeight / CANVAS_HEIGHT;
      return toRound(value / ratio);
    },
    handleClickShape(shape) {
      this.shapeType = shape;
      this.AnnotationFabric[this.type]({ shape });
    },
    handleWindowResize() {
      const MARGIN = {
        VIDEO_AND_TIMELINE_GAP: 20,
        X: 32,
        Y: 32,
      };
      const RIGHT_SIDE_WIDTH = 300;
      const TIMELINE_HEIGHT = 56;
      const MIN_WIDTH = 676;
      const MIN_HEIGHT = 380;
      const innerHeight = this.$refs.annotation.offsetHeight;
      const innerWidth = this.$refs.annotation.offsetWidth;
      const maxVideoHeight = innerHeight - MARGIN.VIDEO_AND_TIMELINE_GAP - TIMELINE_HEIGHT - MARGIN.Y * 2;
      const maxVideoWidth = innerWidth - RIGHT_SIDE_WIDTH - MARGIN.X * 2;
      const scaleRatio = Math.min(maxVideoWidth / MIN_WIDTH, maxVideoHeight / MIN_HEIGHT);
      this.videoHeight = MIN_HEIGHT * scaleRatio;
      this.videoWidth = MIN_WIDTH * scaleRatio;
      this.refreshDataArrayAndFabric(this.dataArray);
    },
    handleEditorToolClear() {
      this.$nextTick(() => {
        this.toolStyle.display = "none";
        this.watch.selectedId = null;
      });
    },
    handleEditorToolPopup(event) {
      const supportToolPopupTypes = [
        ANNOTATION_TYPE.ARROW,
        ANNOTATION_TYPE.PEN,
        ANNOTATION_TYPE.TEXT,
        ANNOTATION_TYPE.SHAPE,
      ];
      const selectableTypes = [...supportToolPopupTypes, ANNOTATION_TYPE.IMAGE, ANNOTATION_TYPE.BLUR];
      let target = null;
      if (event.selected) {
        for (let i = 0; i < event.selected.length; i++) {
          const object = event.selected[i];
          if (selectableTypes.includes(object.type)) {
            target = object;
          }
        }
      } else if (event.target && selectableTypes.includes(event.target.type)) {
        target = event.target;
      }
      this.toolStyle.display = "none";
      if (!target) {
        return;
      }
      const { top, left, id, type } = target;
      if (supportToolPopupTypes.includes(type) && top > 0 && left > 0) {
        const toolHeight = type === ANNOTATION_TYPE.SHAPE ? 135 : 48;
        this.toolStyle.top = top - toolHeight - 8; // space: 8;
        this.toolStyle.left = left;
        this.toolStyle.display = "block";
      }

      this.watch.selectedId = id;
    },
    handleAddDataIntoDataArray(data) {
      const {
        id = Math.random().toString(16).slice(2),
        scaleX = 1,
        scaleY = 1,
        width,
        height,
        ...dataTarget
      } = data.target;
      if (dataTarget.isRedraw || dataTarget.isDrawing) {
        return;
      }
      const startTime = this.currentTime;
      const endTime = startTime + 20 > this.videoEndTime ? this.videoEndTime : startTime + 20;
      const currentType = dataTarget.type || this.type;
      const annotation = {
        id,
        type: currentType,
        startTime,
        endTime,
        top: this.scaleValueToDefaultCanvas(dataTarget.top),
        left: this.scaleValueToDefaultCanvas(dataTarget.left),
        width: this.scaleValueToDefaultCanvas(width),
        height: this.scaleValueToDefaultCanvas(height),
        scaleX,
        scaleY,
        originalCanvasWidth: CANVAS_WIDTH,
        originalCanvasHeight: CANVAS_HEIGHT,
        ...(ANNOTATION_TYPE.PEN === currentType && {
          path: dataTarget.path.map((p) => p.join(" ")).join(" "),
          fill: dataTarget.fill,
          strokeWidth: dataTarget.strokeWidth,
          stroke: dataTarget.stroke,
          // path will record currect width and height so system should keep width/height and scaleX, scaleY
          width,
          height,
          scaleX: this.scaleValueToDefaultCanvas(scaleX),
          scaleY: this.scaleValueToDefaultCanvas(scaleY),
        }),
        ...(ANNOTATION_TYPE.ARROW === currentType && {
          strokeWidth: dataTarget.strokeWidth,
          stroke: dataTarget.stroke,
          startX: this.scaleValueToDefaultCanvas(dataTarget.startX),
          startY: this.scaleValueToDefaultCanvas(dataTarget.startY),
          endX: this.scaleValueToDefaultCanvas(dataTarget.endX),
          endY: this.scaleValueToDefaultCanvas(dataTarget.endY),
        }),
        ...(ANNOTATION_TYPE.IMAGE === currentType && {
          imageUrl: dataTarget.src,
          // use original image width and height and use scaleX/scaleY to adjust size
          width,
          height,
          scaleX: this.scaleValueToDefaultCanvas(scaleX),
          scaleY: this.scaleValueToDefaultCanvas(scaleY),
        }),
        ...(ANNOTATION_TYPE.TEXT === currentType && {
          fill: dataTarget.fill,
          backgroundColor: dataTarget.backgroundColor,
          text: dataTarget.text,
          fontWeight: dataTarget.fontWeight,
          fontStyle: dataTarget.fontStyle,
        }),
        ...(ANNOTATION_TYPE.SHAPE === currentType && {
          fill: dataTarget.fill,
          shape: dataTarget.shape,
          stroke: dataTarget.stroke,
          strokeType: dataTarget.strokeType,
          radius: dataTarget.radius ? this.scaleValueToDefaultCanvas(dataTarget.radius) : null,
        }),
        ...(ANNOTATION_TYPE.BLUR === currentType && {
          shape: dataTarget.shape,
          radius: dataTarget.radius ? this.scaleValueToDefaultCanvas(dataTarget.radius) : null,
        }),
      };

      this.ADD_ANNOTATIONS({ annotation, stepId: this.currentStepId });
      this.$emit("update:madeChanges", true);

      this.watch.selectedId = id;
    },
    handleImageChange(event) {
      const files = event.target.files;
      if (files.length === 0) return;
      const [file] = files;
      const MAX_SIZE_15_MB = 15 * 1000 * 1000;
      const isFileSzieOverLimit = file.size > MAX_SIZE_15_MB;
      if (isFileSzieOverLimit) {
        this.isShowImageSizeLimitAlert = true;
        this.type = ANNOTATION_TYPE.SELECT;
        return;
      }
      const imageUrl = URL.createObjectURL(file);
      this.AnnotationFabric[ANNOTATION_TYPE.IMAGE]({ imageUrl });
      event.target.value = null;
    },
    handleKeyboard(result) {
      const { key, srcElement } = result;
      const isNoteInputEditing = ["INPUT", "TEXTAREA"].includes(srcElement.tagName);
      if (isNoteInputEditing) return;
      this.handleKeyboardPressDelete(key);
      this.handleKeyboardPressArrow(key);
    },
    handleKeyboardPressArrow(key) {
      if (this.isVideoPlaying) return;
      if (!["ArrowLeft", "ArrowRight"].includes(key)) return;
      const targetValue = key === "ArrowLeft" ? -0.01 : 0.01;
      const nextTime = Number((this.currentTime + targetValue).toFixed(2));
      this.handleVideoCurrentTimeUpdate(nextTime);
    },
    handleKeyboardPressDelete(key) {
      if (!this.watch.selectedId) return;
      if (["Backspace", "Delete"].includes(key)) {
        this.handleNoteDelete(this.watch.selectedId);
      }
    },
    handleNoteDelete(targetId) {
      if (this.checkIsWorkflowPublished()) return;
      this.AnnotationFabric.deleteDataArray(targetId);
      this.DELETE_ANNOTATION({
        annotationId: targetId,
        stepId: this.currentStepId,
      });
      this.$emit("update:madeChanges", true);
      if (targetId === this.watch.selectedId) {
        this.watch.selectedId = null;
      }
    },
    handleNoteSwitch() {
      this.isHideCanvas = !this.isHideCanvas;
      this.AnnotationFabric.changeIsHideCanvas(this.isHideCanvas);
      this.AnnotationFabric.detectIsAnnotationShowUp(this.currentTime, this.currentStepId);
    },
    handleNoteSelect(targetData) {
      if (this.checkIsWorkflowPublished()) return;
      if (this.isHideCanvas) this.handleNoteSwitch();
      const { startTime, id } = targetData;
      this.handleVideoCurrentTimeUpdate(startTime);
      this.watch.selectedId = id;
    },
    handleClickBlurItem(id) {
      this.watch.selectedId = id;
    },
    handleNoteTimeUpdate({ id, key, value }) {
      this.MUTATE_ANNOTATION_PROPERTY({
        stepId: this.currentStepId,
        key,
        value,
        id,
      });
      this.$emit("update:madeChanges", true);
    },
    handleNoteStyleChange({ id, props }) {
      Object.entries(props).forEach(([key, value]) => {
        this.MUTATE_ANNOTATION_PROPERTY({
          stepId: this.currentStepId,
          key,
          value,
          id,
        });
      });
      this.refreshDataArrayAndFabric(this.dataArray);
    },
    handleVideoCurrentTimeUpdate(timeSecond) {
      this.$refs.annotationVideoPlayer.$refs.videoPlayer.currentTime = timeSecond;
    },
    handleVideoPlayOrPause() {
      if (this.isVideoPlaying) {
        this.isVideoPlaying = false;
        this.$refs.annotationVideoPlayer.pause();
      } else {
        this.isVideoPlaying = true;
        this.$refs.annotationVideoPlayer.play();
      }
    },
    handleVideoEnd(event) {
      if (event) {
        this.handleVideoPause();
        this.currentTime = this.videoEndTime;
      }
    },
    handleVideoPause() {
      if (this.isVideoPlaying) {
        this.isVideoPlaying = false;
        this.$refs.annotationVideoPlayer.pause();
      }
    },
    handleVideoOnTimeUpdate() {
      // prevent currentTime will over duration (unit: centi seconds)
      const currentVideoTime = this.$refs.annotationVideoPlayer.$refs.videoPlayer.currentTime;
      const currentTime = currentVideoTime > this.videoEndTime ? this.videoEndTime : currentVideoTime;
      this.currentTime = currentTime;
    },
    handleObjectChange(event) {
      const { action, target } = event;
      let keys = [];
      switch (action) {
        case "drag":
          keys = ["top", "left"];
          break;
        case "scaleX":
          keys = ["scaleX", "left"];
          this.handleEditorToolPopup(event);
          break;
        case "scaleY":
          keys = ["scaleY", "top"];
          this.handleEditorToolPopup(event);
          break;
        case "scale":
          keys = ["scaleX", "scaleY", "top", "left"];
          this.handleEditorToolPopup(event);
          break;
        default:
          keys = ["top", "left", "width", "height"];
      }
      if (target.type === ANNOTATION_TYPE.TEXT) {
        keys.push("text");
      }

      if (keys.length) {
        this.mutateAnnotationProperty(keys, target);
      }
    },
    mutateAnnotationProperty(keys = [], target) {
      keys.forEach((key) => {
        const value = target[key];
        const shouldScaleKeys = ["top", "left", "width", "height", "scaleX", "scaleY"];
        this.MUTATE_ANNOTATION_PROPERTY({
          stepId: this.currentStepId,
          key,
          value: shouldScaleKeys.includes(key) ? this.scaleValueToDefaultCanvas(value) : value,
          id: target.id,
        });
      });
      this.$emit("update:madeChanges", true);
    },
    refreshDataArrayAndFabric(dataArray) {
      if (!this.isFontReady) return;
      // reset hide status
      if (this.isHideCanvas) {
        this.isHideCanvas = false;
        this.AnnotationFabric.changeIsHideCanvas(this.isHideCanvas);
      }
      if (dataArray) {
        this.$nextTick(() => {
          this.AnnotationFabric.handleResize({
            width: this.videoWidth,
            height: this.videoHeight,
          });
          this.AnnotationFabric.loadData(dataArray);
          this.AnnotationFabric.detectIsAnnotationShowUp(this.currentTime, this.currentStepId);
        });
      }
    },
  },
  watch: {
    "watch.selectedId": {
      handler(selectedId) {
        if (selectedId) {
          this.AnnotationFabric.selectDataArray(selectedId);
          this.switchType(ANNOTATION_TYPE.SELECT);
        }
        this.AnnotationFabric.toggleBlurItem(selectedId);
      },
    },
    currentTime: {
      immediate: true,
      handler(currentTime) {
        if (this.AnnotationFabric) {
          this.AnnotationFabric.detectIsAnnotationShowUp(currentTime, this.currentStepId);
        }
      },
    },
    currentStepId: {
      immediate: true,
      handler(currentStepId) {
        this.handleVideoPause();
      },
    },
    dataArray: {
      handler(newItems, oldItems) {
        // refresh when stepId change or add/remove annotation item
        this.refreshDataArrayAndFabric(newItems);
      },
    },
    isFontReady(isFontReady) {
      if (isFontReady) {
        this.refreshDataArrayAndFabric(this.dataArray);
      }
    },
    type(type, previousType) {
      this.AnnotationFabric.toggleAnnotationEvent(type === ANNOTATION_TYPE.SELECT);
      this.AnnotationFabric.unbindMouseEvent(previousType);
    },
  },
};
</script>

<style scoped lang="scss">
.annotation {
  --video-height: 380px;
  --video-width: 676px;
  --notes-width: 390px;
  --time-line-height: 60px;
  position: relative;
  display: flex;
  flex-direction: row;
  max-height: 100%;
  height: 100%;
  padding: 32px;
  gap: 24px;

  &__section {
    position: relative;
    width: var(--video-width);
    height: 100%;
    min-width: var(--video-width);
    min-height: calc(var(--video-height) + var(--time-line-height));
  }

  &__hr {
    width: 1px;
    height: 100%;
    background: var(--dGrey4-color);
  }

  &__section ::v-deep .canvas-container {
    position: absolute !important;
    z-index: 10;
  }

  &__video {
    position: relative;
    background: black;
    width: var(--video-width);
    height: var(--video-height);
  }

  &__video video {
    max-width: 100%;
    max-height: 100%;
  }

  &__spinner {
    position: absolute;
    display: flex;
    width: 100%;
    height: 100%;
    justify-content: center;
    align-items: center;
    top: 0;
  }

  &__canvas {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: var(--video-height);
  }

  &__canvas--disabled {
    position: absolute;
    height: var(--video-height);
    width: 100%;
    top: 0;
    left: 0;
    z-index: 25;
  }

  &__canvas--hidden {
    z-index: -1;
  }

  &__control {
    position: relative;
    display: flex;
    flex-direction: row;
    flex: 1;
    gap: 16px;
  }

  &__control-btn {
    width: 40px;
  }

  &__rightBlock {
    width: 100%;
    min-width: var(--notes-width);
    max-width: max(var(--notes-width), 30%);
    display: flex;
    flex-direction: column;
  }

  &__time-line {
    width: var(--video-width);
  }

  &__target-tool {
    background-color: #2c2d32;
    border-radius: 4px;
  }
}

.shapeTypeSelector {
  position: absolute;
  display: flex;
  flex-direction: row;
  bottom: 48px;
  background-color: #2c2d32;
  gap: 4px;
  align-items: center;
  padding: 4px;
  border-radius: 4px;
  .shapeIcon {
    width: 32px;
    height: 32px;
    font-size: 20px;
    display: flex;
    align-items: center;
    justify-content: center;
    &:hover {
      border-radius: 4px;
      background: rgba(255, 255, 255, 0.1);
    }
  }
}

.blurItem {
  position: absolute;
  background: rgba(255, 255, 255, 0);
  backdrop-filter: blur(20px);
  z-index: 11;
}
</style>
